import React, { useEffect, useId, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import apiService from '../../services/api';

interface Suggestion {
  placeId: string;
  description: string;
}

let hasWarnedUnavailable = false;

const warnSuggestionsUnavailableOnce = () => {
  if (!hasWarnedUnavailable) {
    hasWarnedUnavailable = true;
    console.warn(
      'LocationAutocomplete: location suggestions are unavailable. ' +
        'Manual entry still works.'
    );
  }
};

// Google bills autocomplete per session — one token covers a burst of keystrokes
// until the user picks (or clears) a location.
const createSessionToken = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const LOCATION_HINT = 'Pick a suggestion or type a city and country';

interface LocationAutocompleteProps {
  value: string;
  /**
   * Hears every edit as typed, and the trimmed text on blur or Enter. The
   * typed text is the value: suggestions only help write it, so a buyer whose
   * place Google does not know (or whose suggestions never load) still has one.
   */
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  regionCodes?: string[];
  /**
   * Google place types to restrict suggestions to, e.g. ['(regions)'] for
   * cities, states and countries rather than street addresses. Passed straight
   * through to the proxy; omitted means Google's default mix.
   */
  types?: string[];
  disabled?: boolean;
  /** The line under the field; the default says free text is fine. */
  hint?: React.ReactNode;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Search locations...',
  className = '',
  regionCodes,
  types,
  disabled = false,
  hint = LOCATION_HINT,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // Nothing is highlighted until the buyer arrows into the list, so Enter
  // keeps what they typed ("India") instead of swapping in the top match.
  const [activeIndex, setActiveIndex] = useState(-1);

  const hintId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef<string | null>(null);
  // The text a pick just wrote: no lookup for it, it is already a place.
  const pickedRef = useRef<string | null>(null);
  // Only the newest lookup may open the list; a slow answer for an older
  // keystroke, or one landing after the buyer moved on, is dropped.
  const requestRef = useRef(0);

  // Sync with value from parent (e.g. if updated via Redux or loaded initially)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const query = inputValue.trim();
    if (query.length < 2 || inputValue === pickedRef.current) {
      requestRef.current += 1;
      setSuggestions([]);
      return undefined;
    }

    const request = ++requestRef.current;
    const fetchSuggestions = async () => {
      try {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = createSessionToken();
        }
        const { predictions } = await apiService.getPlaceAutocomplete(
          query,
          sessionTokenRef.current,
          regionCodes,
          types
        );
        if (request !== requestRef.current) return;
        setSuggestions(predictions);
        setShowDropdown(predictions.length > 0 && document.activeElement === inputRef.current);
      } catch {
        // Backend unavailable / key unset (503): no suggestions, one console
        // warning; the typed text is already the value.
        warnSuggestionsUnavailableOnce();
        if (request !== requestRef.current) return;
        setSuggestions([]);
        setShowDropdown(false);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(delayDebounce);
  }, [inputValue, regionCodes, types]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  // Close dropdown when clicking outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeList = () => {
    requestRef.current += 1;
    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleInputChange = (val: string) => {
    pickedRef.current = null;
    setInputValue(val);
    // Every edit is the value at once — editing after a pick replaces it.
    onChange(val);
    if (!val) {
      sessionTokenRef.current = null;
      closeList();
    }
  };

  const commitTyped = () => {
    const trimmed = inputValue.trim();
    if (trimmed !== inputValue) setInputValue(trimmed);
    if (trimmed !== value) onChange(trimmed);
  };

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    sessionTokenRef.current = null; // Selection ends the Google billing session
    pickedRef.current = suggestion.description;
    setInputValue(suggestion.description);
    onChange(suggestion.description);
    closeList();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const listOpen = showDropdown && suggestions.length > 0;
    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setShowDropdown(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setShowDropdown(true);
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (listOpen && activeIndex >= 0) handleSelectSuggestion(suggestions[activeIndex]);
      else {
        commitTyped();
        closeList();
      }
    } else if (e.key === 'Escape' && listOpen) {
      e.preventDefault();
      setShowDropdown(false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Focus moving to the clear button: it commits its own empty value.
    if (containerRef.current?.contains(e.relatedTarget as Node | null)) return;
    commitTyped();
    setShowDropdown(false);
  };

  const handleClear = () => {
    sessionTokenRef.current = null;
    pickedRef.current = null;
    setInputValue('');
    onChange('');
    closeList();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-[14px] w-[14px] text-ink-muted" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          role="combobox"
          aria-expanded={showDropdown && suggestions.length > 0}
          aria-autocomplete="list"
          aria-describedby={hint ? hintId : undefined}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
          disabled={disabled}
          placeholder={placeholder}
          className={`${className} w-full pr-10`}
        />

        {inputValue && !disabled && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear location"
              className="p-1 rounded-round text-ink-muted hover:text-ink-secondary hover:bg-surface-hover transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {hint && (
        <p id={hintId} className="mt-s2 text-meta text-ink-muted">
          {hint}
        </p>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div
          role="listbox"
          className="absolute top-9 z-50 w-full mt-s2 bg-surface border border-line rounded-card shadow-floating max-h-60 overflow-y-auto divide-y divide-line-divider"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={index === activeIndex}
              ref={(el) => {
                if (index === activeIndex) el?.scrollIntoView?.({ block: 'nearest' });
              }}
              // Keeps focus in the input, so its blur does not close the list under the click.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-s4 py-s3 flex items-start gap-s3 text-left transition-colors group ${
                index === activeIndex ? 'bg-action-bg' : ''
              }`}
            >
              <MapPin
                className={`h-[14px] w-[14px] mt-[2px] flex-shrink-0 transition-colors ${
                  index === activeIndex ? 'text-action' : 'text-ink-muted'
                }`}
              />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-body-md font-medium truncate ${
                    index === activeIndex ? 'text-action' : 'text-ink'
                  }`}
                >
                  {suggestion.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
